import React from 'react';
import { AlertTriangle, RefreshCw, ExternalLink, X, Clock, CreditCard } from 'lucide-react';
import type { EnrichedStreamError, SubscriptionErrorType } from '../../types/subscription-errors';
import { getSubscriptionErrorMessage } from '../../types/subscription-errors';

interface SubscriptionErrorBannerProps {
  /** Erreur d'abonnement enrichie */
  error: EnrichedStreamError;
  
  /** Callback pour fermer/ignorer l'erreur */
  onDismiss?: () => void;
  
  /** Callback pour réessayer */
  onRetry?: () => void;
  
  /** Mode compact pour affichage dans un espace restreint */
  compact?: boolean;
}

/**
 * Bannière d'affichage des erreurs d'abonnement/quota
 * Affiche des messages contextuels et des actions appropriées
 */
export function SubscriptionErrorBanner({
  error,
  onDismiss,
  onRetry,
  compact = false
}: SubscriptionErrorBannerProps) {
  const subscriptionError = error.subscriptionError;
  
  if (!error.isSubscriptionError || !subscriptionError) {
    // Erreur générique (non liée à l'abonnement)
    return (
      <div className={`
        bg-red-500/10 border border-red-500/20 rounded-lg
        ${compact ? 'p-2' : 'p-4'}
      `}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className={`font-medium text-red-800 dark:text-red-200 ${compact ? 'text-sm' : ''}`}>
              Erreur
            </p>
            <p className={`text-red-700 dark:text-red-300 ${compact ? 'text-xs' : 'text-sm'} mt-1`}>
              {error.message}
            </p>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1 hover:bg-red-500/20 rounded transition-colors"
              aria-label="Fermer"
            >
              <X className="h-4 w-4 text-red-500" />
            </button>
          )}
        </div>
        {onRetry && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={onRetry}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-800 dark:text-red-200 rounded-lg text-sm transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Réessayer
            </button>
          </div>
        )}
      </div>
    );
  }

  // Erreur d'abonnement spécifique
  const { title, description } = getSubscriptionErrorMessage(subscriptionError.error_type);
  const { icon: Icon, bgColor, borderColor, textColor, iconColor } = getErrorStyles(subscriptionError.error_type);

  return (
    <div className={`
      ${bgColor} ${borderColor} border rounded-lg
      ${compact ? 'p-2' : 'p-4'}
    `}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <p className={`font-medium ${textColor} ${compact ? 'text-sm' : ''}`}>
            {title}
          </p>
          <p className={`${textColor} opacity-90 ${compact ? 'text-xs' : 'text-sm'} mt-1`}>
            {description}
          </p>
          
          {/* Informations supplémentaires */}
          {!compact && (
            <div className="mt-2 space-y-1">
              {subscriptionError.remaining_quota !== undefined && subscriptionError.total_quota && (
                <p className={`text-xs ${textColor} opacity-75`}>
                  Quota: {subscriptionError.remaining_quota}/{subscriptionError.total_quota} requêtes
                </p>
              )}
              {subscriptionError.quota_reset_date && (
                <p className={`text-xs ${textColor} opacity-75 flex items-center gap-1`}>
                  <Clock className="h-3 w-3" />
                  Réinitialisation: {formatDate(subscriptionError.quota_reset_date)}
                </p>
              )}
              {subscriptionError.expiration_date && (
                <p className={`text-xs ${textColor} opacity-75 flex items-center gap-1`}>
                  <Clock className="h-3 w-3" />
                  Expiration: {formatDate(subscriptionError.expiration_date)}
                </p>
              )}
              {subscriptionError.current_plan && (
                <p className={`text-xs ${textColor} opacity-75`}>
                  Plan actuel: {subscriptionError.current_plan}
                </p>
              )}
            </div>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`p-1 hover:${bgColor.replace('/10', '/30')} rounded transition-colors`}
            aria-label="Fermer"
          >
            <X className={`h-4 w-4 ${iconColor}`} />
          </button>
        )}
      </div>

      {/* Actions */}
      {subscriptionError.requires_action && (
        <div className={`mt-3 flex flex-wrap gap-2 ${compact ? 'justify-end' : 'justify-between'}`}>
          {onRetry && !['subscription_expired', 'subscription_inactive'].includes(subscriptionError.error_type) && (
            <button
              onClick={onRetry}
              className={`flex items-center gap-2 px-3 py-1.5 ${bgColor.replace('/10', '/20')} hover:${bgColor.replace('/10', '/30')} ${textColor} rounded-lg text-sm transition-colors`}
            >
              <RefreshCw className="h-4 w-4" />
              Réessayer
            </button>
          )}
          
          {subscriptionError.subscription_renewal_url && (
            <div className="flex flex-wrap gap-2">
              <a
                href={subscriptionError.subscription_renewal_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm transition-colors"
              >
                <CreditCard className="h-4 w-4" />
                {subscriptionError.error_type === 'quota_exhausted' 
                  ? 'Augmenter le quota' 
                  : 'Renouveler l\'abonnement'}
                <ExternalLink className="h-3 w-3" />
              </a>
              {subscriptionError.error_type === 'quota_exhausted' && (
                <a
                  href={`${subscriptionError.subscription_renewal_url}?tokens=premium`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-800 dark:text-amber-200 rounded-lg text-sm transition-colors"
                >
                  🪙 Acheter des tokens premium
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
          
          {subscriptionError.suggested_plan && !subscriptionError.subscription_renewal_url && (
            <div className={`text-xs ${textColor} opacity-75`}>
              Plan suggéré: {subscriptionError.suggested_plan}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Obtient les styles visuels pour un type d'erreur
 */
function getErrorStyles(errorType: SubscriptionErrorType) {
  switch (errorType) {
    case 'quota_exhausted':
      return {
        icon: AlertTriangle,
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/20',
        textColor: 'text-amber-800 dark:text-amber-200',
        iconColor: 'text-amber-500'
      };
    case 'subscription_expired':
    case 'subscription_inactive':
      return {
        icon: CreditCard,
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/20',
        textColor: 'text-red-800 dark:text-red-200',
        iconColor: 'text-red-500'
      };
    case 'plan_limit_reached':
      return {
        icon: AlertTriangle,
        bgColor: 'bg-orange-500/10',
        borderColor: 'border-orange-500/20',
        textColor: 'text-orange-800 dark:text-orange-200',
        iconColor: 'text-orange-500'
      };
    case 'payment_required':
      return {
        icon: CreditCard,
        bgColor: 'bg-purple-500/10',
        borderColor: 'border-purple-500/20',
        textColor: 'text-purple-800 dark:text-purple-200',
        iconColor: 'text-purple-500'
      };
    default:
      return {
        icon: AlertTriangle,
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500/20',
        textColor: 'text-red-800 dark:text-red-200',
        iconColor: 'text-red-500'
      };
  }
}

/**
 * Formate une date ISO en format lisible
 */
function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return isoDate;
  }
}

export default SubscriptionErrorBanner;
